import {
  VEHICLE_CATALOG_ERROR_CODES,
  type VehicleCatalogEntryStatus,
  type VehicleCatalogErrorCode,
} from '@carcheck/shared';
import axios from 'axios';

import { errorCodeOf } from '../../../api/errors';

/**
 * Error body of a failed «Справочник авто» write (AUT-437) → what the UI
 * does with it. Modelled on `companies/_lib/companyErrors.ts`; the codes are
 * the shared `VEHICLE_CATALOG_ERROR_CODES` plus `not_found` /
 * `validation_failed`. The writes opt out of the shared auto-toast, so the
 * caller shows exactly one message for each outcome:
 *
 *   - `conflict` (409 name / alias) — inline on the field; `holder` names the
 *     entry that already has the key, absent when a unique-index race caught
 *     it (then the generic «имя занято»);
 *   - `generationConflict` (409 `catalog_generation_conflict`, AUT-450) —
 *     `hit` is the generation that already has the (generation, restyling,
 *     market) key and its status (a hidden one → «Показать снова»); absent
 *     when a unique-index race caught it;
 *   - `yearOrder` (400 `validation_failed` with `details.field: 'yearTo'`) —
 *     «Год по» before «Год с» on the merged generation row;
 *   - `stale` (409 `catalog_stale_write`) — the banner + a refetch;
 *   - `tooLong` (422) — a field error;
 *   - `notFound` (404) — close the drawer, refresh the list;
 *   - `toast` — anything else, with an i18n key.
 */
export interface CatalogHolder {
  makeId: string;
  modelId: string | null;
  name: string;
}

export interface GenerationConflictHit {
  generationId: string;
  status: VehicleCatalogEntryStatus;
}

export type CatalogWriteError =
  | {
      kind: 'conflict';
      code: 'catalog_name_conflict' | 'catalog_alias_conflict';
      holder: CatalogHolder | null;
    }
  | { kind: 'generationConflict'; hit: GenerationConflictHit | null }
  | { kind: 'yearOrder' }
  | { kind: 'stale' }
  | { kind: 'tooLong' }
  | { kind: 'notFound' }
  | { kind: 'toast'; key: string };

const KNOWN = new Set<string>(VEHICLE_CATALOG_ERROR_CODES);

const isCatalogCode = (code: string | null): code is VehicleCatalogErrorCode =>
  code !== null && KNOWN.has(code);

const detailsOf = (err: unknown): Record<string, unknown> | null => {
  if (!axios.isAxiosError(err)) return null;
  const details = (err.response?.data as { details?: unknown } | undefined)?.details;
  return typeof details === 'object' && details !== null
    ? (details as Record<string, unknown>)
    : null;
};

const holderOf = (err: unknown): CatalogHolder | null => {
  const d = detailsOf(err);
  if (!d) return null;
  if (typeof d.makeId !== 'string' || typeof d.name !== 'string') return null;
  const modelId = typeof d.modelId === 'string' ? d.modelId : null;
  return { makeId: d.makeId, modelId, name: d.name };
};

const hitOf = (err: unknown): GenerationConflictHit | null => {
  const d = detailsOf(err);
  if (!d || typeof d.generationId !== 'string') return null;
  if (d.status !== 'active' && d.status !== 'hidden') return null;
  return { generationId: d.generationId, status: d.status };
};

const statusOf = (err: unknown): number | null =>
  axios.isAxiosError(err) ? (err.response?.status ?? null) : null;

export const catalogWriteError = (err: unknown): CatalogWriteError => {
  const code = errorCodeOf(err);
  if (isCatalogCode(code)) {
    if (code === 'catalog_stale_write') return { kind: 'stale' };
    if (code === 'catalog_name_too_long') return { kind: 'tooLong' };
    if (code === 'catalog_generation_conflict') {
      return { kind: 'generationConflict', hit: hitOf(err) };
    }
    if (code === 'catalog_kind_mismatch') {
      return { kind: 'toast', key: 'vehicleCatalog.errors.kindMismatch' };
    }
    if (code === 'generations_not_for_moto') {
      return { kind: 'toast', key: 'vehicleCatalog.errors.generationsNotForMoto' };
    }
    return { kind: 'conflict', code, holder: holderOf(err) };
  }
  const status = statusOf(err);
  if (code === 'not_found' || status === 404) return { kind: 'notFound' };
  if (status === 403) return { kind: 'toast', key: 'errors.forbidden' };
  if (code?.startsWith('validation_')) {
    if (detailsOf(err)?.field === 'yearTo') return { kind: 'yearOrder' };
    return { kind: 'toast', key: 'vehicleCatalog.errors.validation' };
  }
  if (axios.isAxiosError(err) && err.response === undefined) {
    return { kind: 'toast', key: 'errors.network' };
  }
  return { kind: 'toast', key: 'vehicleCatalog.errors.generic' };
};
